// The upscaler on /tools/upscale.
//
// The only tool here that turns a picture away. Everything else on /tools gets
// slower with a big file; this one gets slower in a way nobody would sit
// through, so the limit is part of the tool rather than an apology for it, and
// the refusal says how big the picture is, what the limit is and why.

import { upscale, atScale, tooLargeReason, MODEL, SCALE, MAX_INPUT_EDGE } from './tools/upscaler.js';
import { explainLoadError } from './tools/onnx.js';
import { decode, sizeOf, hasAlpha, encode, renameExtension, saveBlob, formatBytes } from './tools/images.js';
import { wirePicker, blockStrayDrops } from './tools/picker.js';
import { wireCopy } from './tools/copy.js';
import { wireOffline } from './tools/offline.js';
import { wireAccountSave } from './tools/account.js';

const FORMATS = {
    'image/png':  { label: 'PNG',  extension: 'png',  quality: undefined },
    'image/webp': { label: 'WebP', extension: 'webp', quality: 0.92 },
    'image/jpeg': { label: 'JPEG', extension: 'jpg',  quality: 0.92 },
};

const el = id => document.getElementById(id);

const dom = {
    zone: el('upZone'),
    input: el('upInput'),
    empty: el('upEmpty'),
    workspace: el('upWorkspace'),
    stage: el('upStage'),
    image: el('upImage'),
    compare: el('upCompare'),
    factor: el('upFactor'),
    format: el('upFormat'),
    status: el('upStatus'),
    statusText: el('upStatusText'),
    progress: el('upProgress'),
    progressBar: el('upProgressBar'),
    error: el('upError'),
    errorText: el('upErrorText'),
    retry: el('upRetry'),
    live: el('upLive'),
    meta: el('upMeta'),
    save: el('upSave'),
    reset: el('upReset'),
    saveAccount: el('toolSaveAccount'),
    savePanel: el('toolSavePanel'),
    saveList: el('toolSaveList'),
    saveError: el('toolSaveError'),
};

// The last thing onStage said, so onProgress can move the bar without having
// to read the label back out of the DOM to keep it.
let stage = '';

const state = {
    file: null,
    image: null,
    sourceUrl: null,
    transparent: false,
    full: null,      // The four times canvas, computed once per picture.
    output: null,    // { blob, name, url } at the chosen factor and format.
    running: false,
};

function setStatus(text, fraction) {
    // The live region hears each new stage once; the bar's percentages are
    // left to the progressbar role, which a screen reader can ask for.
    if (text && text !== stage) dom.live.textContent = text;
    if (text !== undefined) stage = text;
    dom.status.hidden = !stage;
    dom.statusText.textContent = stage;
    dom.progress.hidden = fraction === undefined;
    if (fraction !== undefined) {
        const percent = Math.round(fraction * 100);
        dom.progressBar.style.width = `${percent}%`;
        dom.progress.setAttribute('aria-valuenow', String(percent));
    }
}

/**
 * Show a problem, or clear it with an empty message.
 *
 * @param {string} message
 * @param {object} [options]
 * @param {boolean} [options.retry]   Offer "Try again", for a download that failed.
 * @param {boolean} [options.notice]  A refusal rather than a failure, shown in amber.
 */
function setError(message, { retry = false, notice = false } = {}) {
    dom.error.hidden = !message;
    dom.errorText.textContent = message || '';
    dom.retry.hidden = !(message && retry);
    dom.error.classList.toggle('bg-red-50', !notice);
    dom.error.classList.toggle('dark:bg-red-950/30', !notice);
    dom.error.classList.toggle('border-red-200/50', !notice);
    dom.error.classList.toggle('dark:border-red-800/50', !notice);
    dom.error.classList.toggle('text-red-700', !notice);
    dom.error.classList.toggle('dark:text-red-300', !notice);
    dom.error.classList.toggle('bg-amber-50', notice);
    dom.error.classList.toggle('dark:bg-amber-950/30', notice);
    dom.error.classList.toggle('border-amber-200/50', notice);
    dom.error.classList.toggle('dark:border-amber-800/50', notice);
    dom.error.classList.toggle('text-amber-800', notice);
    dom.error.classList.toggle('dark:text-amber-200', notice);
}

function releaseOutput() {
    if (state.output) URL.revokeObjectURL(state.output.url);
    state.output = null;
}

/**
 * Encode the result at the chosen factor.
 *
 * The network only knows four times. Two and three are that answer resampled
 * down, which is not a compromise: shrinking an upscale keeps the invented
 * detail and loses only the pixels that were carrying it.
 */
async function present() {
    if (!state.full) return;

    const factor = Number(dom.factor.value) || SCALE;
    const format = FORMATS[dom.format.value] || FORMATS['image/png'];
    const canvas = atScale(state.full, factor);

    const blob = await encode(canvas, dom.format.value, format.quality);
    releaseOutput();
    state.output = {
        blob,
        name: renameExtension(state.file.name, format.extension),
        url: URL.createObjectURL(blob),
    };

    if (dom.compare.getAttribute('aria-pressed') !== 'true') {
        dom.image.src = state.output.url;
    }

    // Squares behind the preview only where the picture actually has holes.
    // JPEG has no alpha to keep, so it never gets them.
    dom.stage.classList.toggle('is-transparent',
        state.transparent && dom.format.value !== 'image/jpeg');

    const source = sizeOf(state.image);
    dom.save.disabled = false;
    dom.meta.textContent = `${source.width} x ${source.height} to ${canvas.width} x ${canvas.height}`
        + ` - ${formatBytes(blob.size)} - ${state.output.name}`;
}

async function run() {
    if (!state.image || state.running) return;

    state.running = true;
    dom.save.disabled = true;
    setError('');

    try {
        state.full = await upscale(state.image, {
            onDownload: fraction => setStatus(`Downloading the model, ${formatBytes(MODEL.bytes)} once`, fraction),
            onStage: text => setStatus(text),
            // Leaves the label alone and only moves the bar: onStage has
            // already said which strip this is.
            onProgress: fraction => setStatus(undefined, fraction),
        });
        setStatus('Rendering');
        await present();
        setStatus('');
    } catch (err) {
        setStatus('');
        setError(explainLoadError(err), { retry: true });
    } finally {
        state.running = false;
    }
}

function reset() {
    releaseOutput();
    if (state.sourceUrl) URL.revokeObjectURL(state.sourceUrl);
    if (state.image?.close) state.image.close();

    state.file = null;
    state.image = null;
    state.sourceUrl = null;
    state.transparent = false;
    state.full = null;

    stage = '';
    dom.image.removeAttribute('src');
    dom.workspace.hidden = true;
    dom.empty.hidden = false;
    dom.compare.setAttribute('aria-pressed', 'false');
    dom.save.disabled = true;
    dom.meta.textContent = '';
    setStatus('');
    setError('');
}

async function open([file]) {
    if (!file) return;
    reset();

    state.file = file;
    let image;
    try {
        image = await decode(file);
    } catch (err) {
        dom.empty.hidden = true;
        dom.workspace.hidden = false;
        setError(err.message || 'Could not read this image');
        return;
    }

    // Checked before anything is shown, so a picture that cannot be worked on
    // never gets a workspace that looks like it is about to do something.
    const refuse = tooLargeReason(image);
    if (refuse) {
        if (image.close) image.close();
        dom.empty.hidden = true;
        dom.workspace.hidden = false;
        setError(refuse, { notice: true });
        const source = sizeOf(image);
        dom.meta.textContent = `${source.width} x ${source.height}, over the ${MAX_INPUT_EDGE} pixel limit`;
        return;
    }

    state.image = image;
    state.transparent = hasAlpha(image);
    state.sourceUrl = URL.createObjectURL(file);
    dom.image.src = state.sourceUrl;

    const source = sizeOf(image);
    dom.meta.textContent = `${source.width} x ${source.height}`;

    dom.empty.hidden = true;
    dom.workspace.hidden = false;
    run();
}

function toggleCompare() {
    const showingOriginal = dom.compare.getAttribute('aria-pressed') === 'true';
    const next = !showingOriginal;
    dom.compare.setAttribute('aria-pressed', String(next));

    if (next) {
        dom.image.src = state.sourceUrl;
    } else if (state.output) {
        dom.image.src = state.output.url;
    }
}

function init() {
    if (!dom.zone) return;

    blockStrayDrops();
    wirePicker({ zone: dom.zone, input: dom.input, multiple: false, onFiles: open });

    // The factor and the format only re-encode what is already computed, so
    // neither of them runs the network again.
    for (const control of [dom.factor, dom.format]) {
        control.addEventListener('change', () => {
            present().catch(err => setError(err.message || 'Could not render the result'));
        });
    }

    dom.compare.addEventListener('click', toggleCompare);
    dom.retry.addEventListener('click', () => { setError(''); run(); });
    dom.save.addEventListener('click', () => {
        if (state.output) saveBlob(state.output.blob, state.output.name);
    });
    dom.reset.addEventListener('click', reset);
    wireOffline({
        root: document.getElementById('upOffline'),
        button: document.getElementById('upOfflineButton'),
        note: document.getElementById('upOfflineNote'),
        model: () => MODEL,
    });
    wireCopy({ button: document.getElementById('upCopy'), mirror: dom.save, collect: () => state.output });

    wireAccountSave({
        button: dom.saveAccount,
        panel: dom.savePanel,
        list: dom.saveList,
        error: dom.saveError,
        collect: () => state.output ? [{ blob: state.output.blob, name: state.output.name }] : [],
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
